import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Brain, Cog, Target, Lightbulb, Code, Building2, Rocket, Link as LinkIcon, Users, BookOpen, Presentation, Briefcase, Shield, Search, FileCheck, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { services } from "@/data/mock";

const consultingIcons = [Brain, Cog, Target, Lightbulb];
const developmentIcons = [Code, Building2, Rocket, LinkIcon];
const cybersecurityIcons = [Shield, Search, FileCheck, AlertTriangle];
const trainingIcons = [Users, BookOpen, Presentation, Briefcase];

const ServiceCategory = ({ data, icons, bgClass }) => (
  <div className="py-16 first:pt-0">
    <div className="mb-10">
      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">{data.title}</h2>
      <p className="text-slate-400 max-w-2xl">{data.description}</p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {data.items.map((item, index) => {
        const Icon = icons[index];
        return (
          <Card key={index} className="bg-slate-800/30 border-slate-700 hover:border-cyan-500/30 transition-all duration-300 group">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-500/20 transition-colors">
                  <Icon className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  </div>
);

export default function Services() {
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 to-slate-900" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-cyan-500/5 to-transparent" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Our <span className="text-cyan-400">Services</span>
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed">
              Comprehensive AI and technology solutions tailored to your business needs. From strategy to implementation, we're with you every step of the way.
            </p>
          </div>
        </div>
      </section>

      {/* Services Content */}
      <section className="pb-24">
        <div className="container mx-auto px-6 lg:px-12">
          <ServiceCategory data={services.consulting} icons={consultingIcons} />
          
          <div className="border-t border-slate-800 my-8" />
          
          <ServiceCategory data={services.development} icons={developmentIcons} />
          
          <div className="border-t border-slate-800 my-8" />
          
          <ServiceCategory data={services.cybersecurity} icons={cybersecurityIcons} />
          
          <div className="border-t border-slate-800 my-8" />
          
          <ServiceCategory data={services.training} icons={trainingIcons} />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-950 border-t border-slate-800">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Discuss Your Project</h2>
            <p className="text-slate-400 mb-8">
              Let's explore how our services can address your specific challenges and goals.
            </p>
            <Link to="/consultation">
              <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-semibold px-8 group">
                Book a Consultation
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
